import { useEffect, useRef, useState } from 'react';
import { EditorContent, Node, ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent, mergeAttributes, useEditor, type NodeViewProps, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Code, Heading2, Heading3, ImagePlus, Italic, Link2, List, ListOrdered, Info, Minus, Quote, Redo2, Strikethrough, Table2, Undo2, Video, Youtube, Paperclip,
} from 'lucide-react';
import { CALLOUT_TONES, toEmbedUrl, type RichDoc, UPLOAD_ACCEPT } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { uploadFile } from '@/lib/upload';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Button, Input, Modal } from '@/components/ui';

/** Resolves an uploaded attachment id to a short-lived URL (through the owning feature's endpoint). */
export type ResolveFile = (attachmentId: string) => Promise<string | null>;

function useResolved(resolve: ResolveFile | undefined, id: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => { let on = true; if (id && resolve) void resolve(id).then(u => { if (on) setUrl(u); }).catch(() => undefined); return () => { on = false; }; }, [id, resolve]);
  return url;
}

// ---------------------------------------------------------------- custom nodes
const Callout = Node.create({
  name: 'callout', group: 'block', content: 'block+', defining: true,
  addAttributes: () => ({ tone: { default: 'info' } }),
  parseHTML: () => [{ tag: 'div[data-tone]', getAttrs: el => ({ tone: (el as HTMLElement).dataset.tone }) }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes({ 'data-tone': HTMLAttributes.tone, class: `rt-callout rt-callout-${HTMLAttributes.tone}` }), 0],
  addNodeView: () => ReactNodeViewRenderer(({ node, updateAttributes }: NodeViewProps) => (
    <NodeViewWrapper className={`rt-callout rt-callout-${node.attrs.tone as string}`} data-tone={node.attrs.tone}>
      <select contentEditable={false} aria-label="Callout style" className="float-right rounded border-0 bg-transparent text-xs" value={node.attrs.tone as string} onChange={e => updateAttributes({ tone: e.target.value })}>
        {CALLOUT_TONES.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
      </select>
      <NodeViewContent />
    </NodeViewWrapper>
  )),
});

function makeMedia(resolve?: ResolveFile) {
  const AttImage = Image.extend({
    addAttributes() { return { ...this.parent?.(), attachmentId: { default: null }, width: { default: null }, height: { default: null } }; },
    addNodeView: () => ReactNodeViewRenderer(({ node, selected }: NodeViewProps) => {
      const url = useResolved(resolve, node.attrs.attachmentId as string | null) ?? (node.attrs.src as string | null);
      return <NodeViewWrapper className={`my-2 inline-block ${selected ? 'ring-2 ring-ink' : ''}`}>{url ? <img src={url} alt={node.attrs.alt as string ?? ''} className="max-h-96 rounded-lg" /> : <div className="h-32 w-48 animate-pulse rounded-lg bg-slate-100" />}</NodeViewWrapper>;
    }),
  });
  const VideoNode = Node.create({
    name: 'video', group: 'block', atom: true,
    addAttributes: () => ({ attachmentId: { default: null }, title: { default: '' } }),
    parseHTML: () => [{ tag: 'video[data-attachment-id]', getAttrs: el => ({ attachmentId: (el as HTMLElement).dataset.attachmentId }) }],
    renderHTML: ({ HTMLAttributes }) => ['video', { 'data-attachment-id': HTMLAttributes.attachmentId, controls: 'true' }],
    addNodeView: () => ReactNodeViewRenderer(({ node, selected }: NodeViewProps) => {
      const url = useResolved(resolve, node.attrs.attachmentId as string | null);
      return <NodeViewWrapper className={`my-2 ${selected ? 'ring-2 ring-ink' : ''}`}>{url ? <video src={url} controls className="max-h-96 w-full rounded-lg bg-black" /> : <div className="flex h-40 items-center justify-center rounded-lg bg-slate-100 text-sm text-slate-500"><Video className="mr-2 h-4 w-4" />{(node.attrs.title as string) || 'Video'}</div>}</NodeViewWrapper>;
    }),
  });
  return [AttImage, VideoNode];
}

const Embed = Node.create({
  name: 'embed', group: 'block', atom: true,
  addAttributes: () => ({ url: { default: '' }, title: { default: '' } }),
  parseHTML: () => [{ tag: 'div[data-embed-url]', getAttrs: el => ({ url: (el as HTMLElement).dataset.embedUrl }) }],
  renderHTML: ({ HTMLAttributes }) => ['div', { 'data-embed-url': HTMLAttributes.url }],
  addNodeView: () => ReactNodeViewRenderer(({ node, selected }: NodeViewProps) => {
    const e = toEmbedUrl(node.attrs.url);
    return (
      <NodeViewWrapper className={`rt-embed my-2 ${selected ? 'ring-2 ring-ink' : ''}`}>
        {e ? <iframe src={e.src} title={(node.attrs.title as string) || 'Video'} sandbox="allow-scripts allow-same-origin allow-presentation" className="pointer-events-none" />
          : <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">Unsupported video link</div>}
      </NodeViewWrapper>
    );
  }),
});

const FileNode = Node.create({
  name: 'file', group: 'block', atom: true,
  addAttributes: () => ({ attachmentId: { default: null }, name: { default: 'Attachment' } }),
  parseHTML: () => [{ tag: 'a.rt-file' }],
  renderHTML: ({ HTMLAttributes }) => ['a', { class: 'rt-file', 'data-attachment-id': HTMLAttributes.attachmentId }, HTMLAttributes.name],
  addNodeView: () => ReactNodeViewRenderer(({ node }: NodeViewProps) => (
    <NodeViewWrapper className="my-1"><span className="rt-file inline-flex items-center gap-1"><Paperclip className="h-4 w-4" />{node.attrs.name as string}</span></NodeViewWrapper>
  )),
});

/** Strip transient preview URLs before saving: uploads are referenced by attachment id only. */
export function cleanDoc(doc: RichDoc): RichDoc {
  const rec = (n: RichDoc['content'] extends (infer X)[] | undefined ? X : never): typeof n => {
    const out = { ...n } as typeof n & { attrs?: Record<string, unknown>; content?: unknown[] };
    if (out.type === 'image' && out.attrs?.attachmentId) out.attrs = { ...out.attrs, src: null };
    if (Array.isArray(out.content)) out.content = out.content.map(c => rec(c as never));
    return out;
  };
  return rec(doc as never) as RichDoc;
}

// ---------------------------------------------------------------- toolbar + editor
function TB({ on, active, label, children, disabled }: { on: () => void; active?: boolean; label: string; children: React.ReactNode; disabled?: boolean }) {
  return <button type="button" title={label} aria-label={label} aria-pressed={active} disabled={disabled} onMouseDown={e => e.preventDefault()} onClick={on}
    className={`rounded p-1.5 hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800 ${active ? 'bg-slate-200 dark:bg-slate-700' : ''}`}>{children}</button>;
}

function Toolbar({ editor, purpose, uploads = true, extra }: { editor: Editor; purpose: EditorPurpose; uploads?: boolean; extra?: React.ReactNode }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<null | 'link' | 'embed'>(null);
  const [val, setVal] = useState('');
  const upload = async (files: FileList | null) => {
    if (!files?.length || !ws) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const a = await uploadFile(f, { workspace_id: ws, purpose });
        const local = URL.createObjectURL(f);
        if (f.type.startsWith('image/')) editor.chain().focus().setImage({ src: local, alt: f.name }).updateAttributes('image', { attachmentId: a.id, width: a.width ?? null, height: a.height ?? null }).run();
        else if (f.type.startsWith('video/')) editor.chain().focus().insertContent({ type: 'video', attrs: { attachmentId: a.id, title: f.name } }).run();
        else editor.chain().focus().insertContent({ type: 'file', attrs: { attachmentId: a.id, name: f.name } }).run();
      }
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const c = () => editor.chain().focus();
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-white/95 px-2 py-1 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
      <TB label="Heading" active={editor.isActive('heading', { level: 2 })} on={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></TB>
      <TB label="Subheading" active={editor.isActive('heading', { level: 3 })} on={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></TB>
      <TB label="Bold" active={editor.isActive('bold')} on={() => c().toggleBold().run()}><Bold className="h-4 w-4" /></TB>
      <TB label="Italic" active={editor.isActive('italic')} on={() => c().toggleItalic().run()}><Italic className="h-4 w-4" /></TB>
      <TB label="Strikethrough" active={editor.isActive('strike')} on={() => c().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></TB>
      <TB label="Link" active={editor.isActive('link')} on={() => { setVal((editor.getAttributes('link').href as string) ?? ''); setDialog('link'); }}><Link2 className="h-4 w-4" /></TB>
      <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
      <TB label="Bulleted list" active={editor.isActive('bulletList')} on={() => c().toggleBulletList().run()}><List className="h-4 w-4" /></TB>
      <TB label="Numbered list" active={editor.isActive('orderedList')} on={() => c().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></TB>
      <TB label="Quote" active={editor.isActive('blockquote')} on={() => c().toggleBlockquote().run()}><Quote className="h-4 w-4" /></TB>
      <TB label="Code block" active={editor.isActive('codeBlock')} on={() => c().toggleCodeBlock().run()}><Code className="h-4 w-4" /></TB>
      <TB label="Callout" active={editor.isActive('callout')} on={() => c().wrapIn('callout').run()}><Info className="h-4 w-4" /></TB>
      <TB label="Divider" on={() => c().setHorizontalRule().run()}><Minus className="h-4 w-4" /></TB>
      <TB label="Table" on={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><Table2 className="h-4 w-4" /></TB>
      <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
      {uploads && <TB label={busy ? 'Uploading…' : 'Image, video or file'} disabled={busy} on={() => fileRef.current?.click()}><ImagePlus className="h-4 w-4" /></TB>}
      <TB label="YouTube, Vimeo or Stream video" on={() => { setVal(''); setDialog('embed'); }}><Youtube className="h-4 w-4" /></TB>
      <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
      <TB label="Undo" disabled={!editor.can().undo()} on={() => c().undo().run()}><Undo2 className="h-4 w-4" /></TB>
      <TB label="Redo" disabled={!editor.can().redo()} on={() => c().redo().run()}><Redo2 className="h-4 w-4" /></TB>
      {extra && <><span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />{extra}</>}
      {editor.isActive('table') && <span className="ml-2 flex gap-1 text-xs">
        <button type="button" className="link" onClick={() => c().addRowAfter().run()}>+ row</button><button type="button" className="link" onClick={() => c().addColumnAfter().run()}>+ column</button>
        <button type="button" className="link" onClick={() => c().deleteRow().run()}>− row</button><button type="button" className="link" onClick={() => c().deleteColumn().run()}>− column</button>
        <button type="button" className="link" onClick={() => c().deleteTable().run()}>delete table</button></span>}
      <input ref={fileRef} type="file" multiple className="hidden" accept={UPLOAD_ACCEPT} onChange={e => void upload(e.target.files)} />
      <Modal open={dialog !== null} onClose={() => setDialog(null)} title={dialog === 'link' ? 'Link' : 'Embed a video'}
        footer={<><Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
          {dialog === 'link' && editor.isActive('link') && <Button variant="secondary" onClick={() => { c().unsetLink().run(); setDialog(null); }}>Remove link</Button>}
          <Button disabled={dialog === 'embed' && !toEmbedUrl(val)} onClick={() => {
            if (dialog === 'link') c().extendMarkRange('link').setLink({ href: val }).run();
            else c().insertContent({ type: 'embed', attrs: { url: val } }).run();
            setDialog(null);
          }}>{dialog === 'link' ? 'Save link' : 'Embed'}</Button></>}>
        <Input autoFocus placeholder={dialog === 'link' ? 'https://… or /kb/…' : 'https://www.youtube.com/watch?v=… , vimeo.com/… or a Stream link'} value={val} onChange={e => setVal(e.target.value)} />
        {dialog === 'embed' && val && !toEmbedUrl(val) && <p className="mt-2 text-xs text-rose-700">Paste a YouTube, Vimeo or Microsoft Stream link.</p>}
      </Modal>
    </div>
  );
}

export type EditorPurpose = 'kb' | 'announcement' | 'policy';

export function RichEditor({ value, onChange, purpose, resolve, placeholder, minHeight = 320, uploads = true, toolbarExtra, contentClassName, onEditor }: {
  value: RichDoc; onChange: (d: RichDoc) => void; purpose: EditorPurpose; resolve?: ResolveFile; placeholder?: string; minHeight?: number;
  /** Extra toolbar buttons (policies: insert a reusable block). Receives the editor. */
  toolbarExtra?: (editor: Editor) => React.ReactNode;
  /** Extra classes on the editable area (policies: numbered headings). */
  contentClassName?: string;
  onEditor?: (editor: Editor | null) => void;
  /** Help Centre articles have no file store of their own: links, tables, callouts and video embeds only. */
  uploads?: boolean;
}) {
  const [extensions] = useState(() => [
    StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
    Link.configure({ openOnClick: false, autolink: true, protocols: ['mailto'], HTMLAttributes: { rel: 'noopener noreferrer nofollow' } }),
    ...makeMedia(resolve), Embed, FileNode, Callout,
    Table.configure({ resizable: false }), TableRow, TableHeader, TableCell,
    Placeholder.configure({ placeholder: placeholder ?? 'Write something…' }),
  ]);
  const editor = useEditor({
    extensions, content: value,
    editorProps: { attributes: { class: `rich-content focus:outline-none px-4 py-3 ${contentClassName ?? ''}`, style: `min-height:${minHeight}px` } },
    onUpdate: ({ editor: e }) => onChange(e.getJSON() as RichDoc),
  });
  // External resets (e.g. restore a version) replace the content.
  const last = useRef(value);
  useEffect(() => {
    if (!editor || value === last.current) return;
    last.current = value;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(value)) editor.commands.setContent(value, false);
  }, [editor, value]);
  useEffect(() => { onEditor?.(editor); }, [editor, onEditor]);
  useEffect(() => { if (editor) editor.setOptions({ editorProps: { attributes: { class: `rich-content focus:outline-none px-4 py-3 ${contentClassName ?? ''}`, style: `min-height:${minHeight}px` } } }); }, [editor, contentClassName, minHeight]);
  if (!editor) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900">
      <Toolbar editor={editor} purpose={purpose} uploads={uploads} extra={toolbarExtra?.(editor)} />
      <EditorContent editor={editor} />
    </div>
  );
}
